"use client";

import { Button } from "@mantine/core";
import dayjs from "dayjs";
import { useTranslations } from "next-intl";

import type { SubscriptionData } from "@repo/dashboard-core";

type SubscriptionInfoContentProps = {
  subscription: SubscriptionData;
  setCurrentView: (view: "unsubscribeModal") => void;
  onResubscribe: () => void;
  isResubscribing: boolean;
  handleClose: () => void;
};

export function SubscriptionInfoContent({
  subscription,
  setCurrentView,
  onResubscribe,
  isResubscribing,
  handleClose,
}: SubscriptionInfoContentProps) {
  const t = useTranslations("subscription");
  const status = subscription.status ?? "";
  const startDate = subscription.startDate ?? "";
  const renewalDate = subscription.renewalDate ?? "";

  const isActive = status === "active";
  const isPendingCancellation = status === "pending_cancellation";

  const subStatus: Record<string, string> = {
    active: t("info.status.active"),
    not_active: t("info.status.not-active"),
    cancelled: t("info.status.cancelled"),
    suspended: t("info.status.suspended"),
    upsale: t("info.status.upsale"),
    pending_cancellation: t("info.status.pending-cancellation"),
  };

  const subInfo = [
    { label: t("info.current-status"), value: subStatus[status] ?? status },
    {
      label: t("info.subscribed-since"),
      value: dayjs(startDate).format("DD MMM YYYY"),
    },
    {
      label: t("info.renews-on"),
      value: dayjs(renewalDate).format("DD MMM YYYY"),
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-y-6 lg:w-[447px]">
      <h3 className="text-xl font-semibold">
        {t("info.title", { status: subStatus[status] ?? status })}
      </h3>
      <div className="flex flex-col gap-y-3">
        {subInfo.map((info) => (
          <p
            className="flex w-full justify-between rounded-lg border border-surface-border px-3 py-2"
            key={info.label}
          >
            <span className="text-base font-medium">{info.label}</span>
            <span className="text-base font-light">{info.value}</span>
          </p>
        ))}
      </div>
      <div className="flex w-full flex-col-reverse items-center gap-4 pb-6 sm:flex-row sm:justify-between">
        {isActive ? (
          <>
            <Button
              className="flex-1"
              variant="filled"
              color="dark"
              radius="xl"
              size="md"
              onClick={() => setCurrentView("unsubscribeModal")}
            >
              {t("cta.unsubscribe")}
            </Button>
            <Button
              className="flex-1"
              variant="filled"
              radius="xl"
              size="md"
              onClick={handleClose}
            >
              {t("cta.ok")}
            </Button>
          </>
        ) : (
          <>
            <Button
              className="flex-1"
              variant="filled"
              radius="xl"
              size="md"
              loading={isResubscribing}
              onClick={onResubscribe}
            >
              {t(isPendingCancellation ? "cta.resume" : "cta.restart")}
            </Button>
            <Button
              className="flex-1"
              variant="filled"
              color="dark"
              radius="xl"
              size="md"
              onClick={handleClose}
            >
              {t("cta.close")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
